/**
 * Max Quantity Enforcement
 * Caps per-variant cart quantity; blocks PDP submits and clamps /cart/add.js.
 */
(function () {
  'use strict';
  if (window.__maxQtyEnforcementInitialized) return;
  window.__maxQtyEnforcementInitialized = true;

  const SEL = 'quantity-selector[data-max-quantity]';
  const DATA_SEL = 'script[type="application/json"][data-max-qty-list]';
  const CART_ADD = /\/cart\/add(\.js)?(\?|$)/i;
  const maxMap = new Map();
  const titleMap = new Map();
  const cart = new Map();
  let inflight = null, cartReady = false;

  const num = (v, d = 0) => parseInt(v, 10) || d;
  const inCartOf = (id) => cart.get(String(id)) || 0;
  const remaining = (id, max) => Math.max(0, max - inCartOf(id));

  // Merge a selector's variant list into the global maps.
  function mergeRoot(root) {
    try {
      JSON.parse(root.getAttribute('data-variant-max-list') || '[]').forEach((i) => {
        if (!i || !i.id) return;
        const m = num(i.max);
        if (m > 0) maxMap.set(String(i.id), m);
        if (i.title && !/^default\s*title$/i.test(i.title)) titleMap.set(String(i.id), String(i.title));
      });
    } catch (e) {}
  }

  function refreshCart(force) {
    if (inflight && !force) return inflight;
    inflight = fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then((r) => (r && r.ok ? r.json() : null))
      .then((data) => {
        cart.clear();
        ((data && data.items) || []).forEach((i) => {
          const id = String(i.id || i.variant_id || '');
          if (id) cart.set(id, (cart.get(id) || 0) + num(i.quantity));
        });
        cartReady = true;
        document.querySelectorAll(SEL).forEach((r) => r.__norm && r.__norm());
        syncQuickAdds();
      })
      .catch(() => {})
      .finally(() => { inflight = null; });
    return inflight;
  }

  function notice(root, msg) {
    let n = root.querySelector('.max-qty-notice');
    if (!msg && !n) return;
    if (!n) {
      n = document.createElement('div');
      n.className = 'max-qty-notice';
      n.setAttribute('role', 'status');
      n.style.cssText = 'margin-top:6px;color:#cb0c13;font-size:12px;line-height:1.4';
      root.appendChild(n);
    }
    clearTimeout(n.__t);
    n.textContent = msg || '';
    n.style.display = msg ? '' : 'none';
    if (msg) n.__t = setTimeout(() => notice(root, ''), 6000);
  }

  function bind(root) {
    if (!root || root.__bound) return;
    const max = num(root.getAttribute('data-max-quantity'));
    const input = root.querySelector('.quantity-selector--input');
    const plus = root.querySelector('.quantity-selector--plus');
    if (!max || !input || !plus) return;

    mergeRoot(root);

    const minus = root.querySelector('.quantity-selector--minus');
    const form = root.closest('form');
    const isCart = root.getAttribute('data-type') === 'cart';
    const def = Math.max(1, num(input.defaultValue, 1));
    const vInput = !isCart && form
      ? form.querySelector('.product-buy-buttons--input, input[name="id"], select[name="id"]')
      : null;

    const vid = () => String(root.__vid || (vInput && (vInput.value || vInput.dataset.id)) || '');
    const curMax = () => maxMap.get(vid()) || max;

    function norm(syncBack) {
      const orig = parseInt(input.value);
      const cur = curMax();
      const cap = isCart ? cur : remaining(vid(), cur);
      let v = isNaN(orig) || orig < 1 ? 1 : orig;
      if (cap > 0 && v > cap) v = cap;
      input.value = v;
      input.setAttribute('max', String(cap > 0 ? cap : cur));
      plus.toggleAttribute('disabled', (!isCart && cap === 0) || v >= cap);
      if (minus) minus.toggleAttribute('disabled', v <= 1);
      if (syncBack && isCart && v !== orig && typeof root.callback === 'function') root.callback(v);
    }

    function validate() {
      if (isCart) return true;
      const cur = curMax();
      const id = vid();
      const rem = remaining(id, cur);
      if (rem <= 0) {
        const scope = form || root.closest('section, .product-quick-add--form, .product--root') || document;
        const h = scope.querySelector('.product-form--block--heading, .product-quick-add--title, [data-product-title], .product--title');
        const pt = h ? (h.getAttribute('data-product-title') || h.textContent || '').trim().replace(/\s+/g, ' ') : '';
        const vt = titleMap.get(id) || '';
        const label = pt ? ` of the ${pt}${vt ? ' ' + vt : ''}` : (vt ? ` (${vt})` : '');
        notice(root, `You can only purchase a maximum of ${cur}${label} per order.`);
        return false;
      }
      if (num(input.value, 1) > rem) { input.value = rem; norm(); }
      return true;
    }

    function reset() { input.value = def; notice(root, ''); norm(); }

    root.__bound = true;
    root.__norm = norm;
    root.__reset = reset;
    root.__validate = validate;

    const onChange = () => requestAnimationFrame(() => norm());
    input.addEventListener('input', onChange);
    input.addEventListener('focusout', () => norm(true));
    plus.addEventListener('click', onChange);
    if (minus) minus.addEventListener('click', onChange);

    plus.addEventListener('click', (e) => {
      const cur = curMax();
      if (num(input.value, 1) >= cur) {
        e.preventDefault();
        e.stopPropagation();
        input.value = cur;
        norm();
      }
    }, true);

    if (vInput) {
      const onVar = () => requestAnimationFrame(reset);
      vInput.addEventListener('change', onVar);
      vInput.addEventListener('input', onVar);
      const pid = root.getAttribute('data-id');
      if (pid) {
        window.addEventListener(`theme:product:${pid}:variantChanged`, (e) => {
          const v = e && e.detail && e.detail.variant && e.detail.variant.id;
          if (v) { root.__vid = String(v); reset(); }
        });
      }
    }

    if (form && !form.__satcBound) {
      form.__satcBound = true;
      form.addEventListener('submit', (e) => {
        let blocked = false;
        form.querySelectorAll(SEL).forEach((r) => {
          if (r.getAttribute('data-type') === 'cart') return;
          r.__norm && r.__norm();
          if (r.__validate && !r.__validate()) blocked = true;
        });
        if (blocked) {
          e.preventDefault();
          e.stopPropagation();
          form.querySelectorAll('.product-buy-buttons--primary[aria-busy="true"]').forEach((b) => b.setAttribute('aria-busy', 'false'));
        }
      }, true);
    }

    norm();
  }

  const bindAll = (node = document) => node.querySelectorAll(SEL).forEach(bind);

  function harvest() {
    document.querySelectorAll(DATA_SEL).forEach((el) => {
      try {
        JSON.parse(el.textContent || '[]').forEach((i) => {
          if (i && i.id && num(i.max) > 0) maxMap.set(String(i.id), num(i.max));
        });
      } catch (e) {}
    });
  }

  function syncQuickAdds() {
    if (!cartReady) return;
    document.querySelectorAll('product-quick-add[data-form="false"][data-id]').forEach((el) => {
      const id = el.getAttribute('data-id');
      const max = maxMap.get(String(id));
      if (!max) return;
      const btn = el.querySelector('.product-quick-add--button');
      if (btn) btn.disabled = inCartOf(id) >= max;
    });
  }

  // ── /cart/add.js fetch interceptor ──
  function clamp(id, qty) {
    const max = id && maxMap.get(String(id));
    if (!max) return { qty, blocked: false, changed: false };
    const rem = remaining(id, max);
    if (rem <= 0) return { qty: 0, blocked: true, changed: true };
    if (qty > rem) return { qty: rem, blocked: false, changed: true };
    return { qty, blocked: false, changed: false };
  }

  function clampInit(init) {
    if (!init || typeof init.body !== 'string') return { init, blocked: false };
    const ct = (init.headers && (init.headers['Content-Type'] || init.headers['content-type'])) || '';
    try {
      if (/json/i.test(ct)) {
        const p = JSON.parse(init.body);
        let mut = false, blk = false;
        const handle = (e) => {
          const r = clamp(e && (e.id || e.variant_id), num(e.quantity, 1));
          if (r.changed) { e.quantity = r.qty; mut = true; }
          if (r.blocked) blk = true;
          return e;
        };
        if (Array.isArray(p.items)) {
          p.items = p.items.map(handle).filter((e) => num(e.quantity) > 0);
          if (!p.items.length) blk = true;
        } else if (p.id) {
          handle(p);
          if (num(p.quantity) <= 0) blk = true;
        }
        if (mut || blk) init.body = JSON.stringify(p);
        return { init, blocked: blk };
      }
      if (/x-www-form-urlencoded/i.test(ct)) {
        const params = new URLSearchParams(init.body);
        const r = clamp(params.get('id'), num(params.get('quantity'), 1));
        if (r.blocked) return { init, blocked: true };
        if (r.changed) {
          params.set('quantity', String(r.qty));
          const sect = init.body.split('&sections=')[1];
          init.body = params.toString() + (sect ? '&sections=' + sect.split('&')[0] : '');
        }
      }
    } catch (e) {}
    return { init, blocked: false };
  }

  if (typeof window.fetch === 'function' && !window.__maxQtyFetchPatched) {
    window.__maxQtyFetchPatched = true;
    const orig = window.fetch.bind(window);
    window.fetch = function (resource, init) {
      const url = typeof resource === 'string' ? resource : (resource && resource.url) || '';
      if (!CART_ADD.test(url) || !init || !/post/i.test(init.method || '')) return orig(resource, init);
      // Re-fetch cart before clamping so mini-cart edits can't leave us stale.
      return refreshCart(true).then(() => {
        const r = clampInit(init);
        if (r.blocked) {
          // Return fake-200 instead of 422 so theme's QuickAdd doesn't get a
          // stuck aria-busy spinner from the thrown error.
          syncQuickAdds();
          return new Response(
            JSON.stringify({ items: [], sections: {} }),
            { status: 200, headers: { 'Content-Type': 'application/json' } }
          );
        }
        return orig(resource, r.init).then((res) => { if (res && res.ok) refreshCart(true); return res; });
      });
    };
  }

  // Public API for other scripts (e.g. sticky ATC).
  window.MaxQty = {
    getInCart: inCartOf,
    getMaxFor: (id) => maxMap.get(String(id)) || null,
    getVariantTitle: (id) => titleMap.get(String(id)) || '',
    ensureCartLoaded: () => (cartReady ? Promise.resolve() : refreshCart()),
    refresh: () => refreshCart(true)
  };

  refreshCart();
  harvest();
  bindAll();

  document.addEventListener('variant:changed', () => requestAnimationFrame(() => {
    document.querySelectorAll(SEL).forEach((r) => (r.__reset ? r.__reset() : bind(r)));
  }));

  window.addEventListener('theme:cart:updated', () => {
    refreshCart(true).then(syncQuickAdds);
    requestAnimationFrame(() => bindAll());
  });

  if (document.body) {
    new MutationObserver((muts) => {
      for (const { addedNodes } of muts) {
        for (const n of addedNodes) {
          if (n.nodeType !== 1) continue;
          if (n.matches && n.matches(SEL)) bind(n);
          if (n.querySelectorAll) { bindAll(n); harvest(); syncQuickAdds(); }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }
})();



